import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useIsPro } from "@/hooks/useIsPro";

export type AlertStatus = "out_of_stock" | "critical" | "order_now" | "watch" | "healthy" | "insufficient_data";

export interface ReorderAlert {
  inventory_id: string;
  product_name: string;
  sku: string;
  current_stock: number;
  avg_daily_sales: number;
  days_of_stock_left: number;
  avg_lead_time_days: number | null;
  reorder_point: number;
  suggested_order_qty: number;
  fulfilment_rate: number;
  supplier_id: string | null;
  supplier_name: string | null;
  last_price_paid: number | null;
  alert_status: AlertStatus;
  alert_message: string | null;
}

const STALE_MS = 1000 * 60 * 15;

/** Matches get_reorder_alerts rules for a cached reorder_intelligence row + current stock. */
export function computeReorderAlertStatus(
  currentStock: number,
  row: {
    avg_daily_sales: number | null;
    days_of_stock_left: number | null;
    avg_lead_time_days: number | null;
    reorder_point: number | null;
  }
): AlertStatus | null {
  const avgDaily = Number(row.avg_daily_sales ?? 0);
  if (avgDaily <= 0) return null;
  const leadRaw = row.avg_lead_time_days;
  if (leadRaw == null || Number(leadRaw) <= 0) return "insufficient_data";
  const daysLeft = Number(row.days_of_stock_left ?? 999);
  const lead = Number(leadRaw);
  const rp = Number(row.reorder_point ?? 0);
  if (currentStock === 0) return "out_of_stock";
  if (daysLeft < lead) return "critical";
  if (currentStock <= rp) return "order_now";
  if (currentStock <= rp * 1.3) return "watch";
  return "healthy";
}

export function reorderQueryKeys(businessId: string | null) {
  return {
    alerts: ["reorder-alerts", businessId] as const,
    rows: ["reorder-intelligence-rows", businessId] as const,
  };
}

export function useInvalidateReorderQueries() {
  const qc = useQueryClient();
  const { businessId } = useAuth();
  return () => {
    if (!businessId) return;
    const k = reorderQueryKeys(businessId);
    void qc.invalidateQueries({ queryKey: k.alerts });
    void qc.invalidateQueries({ queryKey: k.rows });
  };
}

export function useReorderAlerts() {
  const { businessId } = useAuth();
  const isPro = useIsPro();

  return useQuery({
    queryKey: reorderQueryKeys(businessId).alerts,
    queryFn: async () => {
      if (!businessId) return [];
      const { error: calcErr } = await supabase.rpc("calculate_reorder_intelligence", {
        p_business_id: businessId,
      });
      if (calcErr) throw calcErr;
      const { data, error } = await supabase.rpc("get_reorder_alerts", { p_business_id: businessId });
      if (error) throw error;
      const rows = (data ?? []) as Record<string, unknown>[];
      return rows.map((r) => ({
        inventory_id: r.inventory_id as string,
        product_name: r.product_name as string,
        sku: r.sku as string,
        current_stock: Number(r.current_stock),
        avg_daily_sales: Number(r.avg_daily_sales),
        days_of_stock_left: Number(r.days_of_stock_left),
        avg_lead_time_days: r.avg_lead_time_days != null ? Number(r.avg_lead_time_days) : null,
        reorder_point: Number(r.reorder_point),
        suggested_order_qty: Number(r.suggested_order_qty),
        fulfilment_rate: Number(r.fulfilment_rate),
        supplier_id: (r.supplier_id as string | null) ?? null,
        supplier_name: (r.supplier_name as string | null) ?? null,
        last_price_paid: r.last_price_paid != null ? Number(r.last_price_paid) : null,
        alert_status: r.alert_status as AlertStatus,
        alert_message: (r.alert_message as string | null) ?? null,
      })) as ReorderAlert[];
    },
    enabled: !!businessId && isPro,
    staleTime: STALE_MS,
  });
}

/** Cached rows only — no calculate RPC (for Inventory chips). */
export function useReorderIntelligenceRows() {
  const { businessId } = useAuth();
  const isPro = useIsPro();

  return useQuery({
    queryKey: reorderQueryKeys(businessId).rows,
    queryFn: async () => {
      if (!businessId) return [];
      const { data, error } = await supabase
        .from("reorder_intelligence")
        .select(
          "inventory_id, avg_daily_sales, days_of_stock_left, avg_lead_time_days, reorder_point, suggested_order_qty, fulfilment_rate, supplier_id"
        )
        .eq("business_id", businessId);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!businessId && isPro,
    staleTime: STALE_MS,
  });
}

export function useReorderAlertCounts() {
  const { data: alerts = [], isLoading } = useReorderAlerts();
  return {
    isLoading,
    out_of_stock: alerts.filter((a) => a.alert_status === "out_of_stock").length,
    critical: alerts.filter((a) => a.alert_status === "critical").length,
    order_now: alerts.filter((a) => a.alert_status === "order_now").length,
    watch: alerts.filter((a) => a.alert_status === "watch").length,
    urgent: alerts.filter(
      (a) =>
        a.alert_status === "out_of_stock" ||
        a.alert_status === "critical" ||
        a.alert_status === "order_now"
    ).length,
  };
}
